import type { AttachMediaMode, AttachTarget } from '../../../types/metaAdDraft';
import { mediaModeOf, reusableMedia } from '../../../lib/metaAttachMedia';

// "Add an ad to a live ad set": the media choice (Part A). Shown with the source thumbnail.
// The thumbnail is for choosing only: "original" sends Meta's image hash / video id, never the URL.
export function AttachMediaChoice({
  target,
  onChange,
}: {
  target: AttachTarget;
  onChange: (mode: AttachMediaMode) => void;
}) {
  const mode = mediaModeOf(target);
  const reuse = reusableMedia(target);
  const options: Array<{ value: AttachMediaMode; label: string; hint: string; disabled: boolean }> = [
    {
      value: 'original',
      label: reuse.original === 'video' ? 'Use the original video' : 'Use the original image',
      hint: reuse.original ? 'Same media as the source ad. The copy stays editable.' : 'The source ad did not return its original media.',
      disabled: reuse.original === null,
    },
    {
      value: 'new',
      label: 'New image or video',
      hint: 'Upload new media (or pull it from Drive / portal assets).',
      disabled: false,
    },
    {
      value: 'exact',
      label: 'Reuse this exact ad',
      hint: 'Attach the source creative unchanged. Nothing about it can be edited.',
      disabled: !reuse.exact,
    },
  ];
  return (
    <section className="space-y-3 rounded-md border border-slate-200 p-4" aria-label="Media">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Media</h3>
      <div className="flex flex-wrap items-start gap-4">
        {target.sourceThumbnailUrl ? (
          <img src={target.sourceThumbnailUrl} alt="Source ad media" className="h-24 w-24 rounded object-cover" />
        ) : null}
        <div role="radiogroup" aria-label="Media choice" className="min-w-0 flex-1 space-y-2">
          {options.map((o) => (
            <label key={o.value} className={['flex items-start gap-2 text-sm', o.disabled ? 'text-slate-400' : 'text-slate-800'].join(' ')}>
              <input
                type="radio"
                name="attach-media-mode"
                checked={mode === o.value}
                disabled={o.disabled}
                onChange={() => onChange(o.value)}
                className="mt-1"
              />
              <span>
                <span className="font-medium">{o.label}</span>
                <span className="block text-xs text-slate-500">{o.hint}</span>
              </span>
            </label>
          ))}
        </div>
      </div>
      <p className="text-xs text-slate-500">Media can only be reused within the same ad account and Facebook Page.</p>
    </section>
  );
}
